import { get as lodashGet, snakeCase } from 'lodash'
import sentry from '@/services/sentry'
import mixpanelContextUtils from '@/utils/mixpanel.context.utils'
import mixpanelUtil from '@/utils/mixpanel.utils.js'

const USER_ACTION_TYPE = 'userAction'
const ROUTE_CHANGE_ACTION_TYPE = 'routeChangeAction'
const SHOW_TOAST_ACTION_TYPE = 'showToastAction'

const SYNC_MUTATION_TYPES = [
  'setCurrentUser',
  'updateCurrentUser',
  'setPresentation',
  'setSlideActive',
  'updateSlideActive',
  'setLang',
  'setSelectedFolderId',
]

const MUTATION_STATES_MAP = {
  setCurrentUser: 'currentUser',
  updateCurrentUser: 'currentUser',
  setPresentation: 'presentation',
  setLang: 'presentation',
  updateSlideActive: 'slideActive',
  setSlideActive: 'slideActive',
  setSelectedFolderId: 'selections',
}

const mixpanelContextSetters = {
  setCurrentUser: mixpanelContextUtils.setUserContext,
  updateCurrentUser: mixpanelContextUtils.setUserContext,
  setPresentation: mixpanelContextUtils.setPresentationContext,
  setLang: mixpanelContextUtils.setPresentationContext,
  updateSlideActive: mixpanelContextUtils.setSlideContext,
  setSlideActive: mixpanelContextUtils.setSlideContext,
  setSelectedFolderId: mixpanelContextUtils.setFolderContext,
}

const eventActions = {
  click: 'click',
  mouseenter: 'hover',
  dblclick: 'double_click',
  view: 'view',
  focus: 'focus',
  blur: 'blur',
  change: 'change',
  submit: 'submit',
}

function _getObjectName(vnode) {
  const nameFromAttr = lodashGet(vnode, 'data.attrs.name', '')
  const nameFromPropsData = lodashGet(vnode, 'componentOptions.propsData.name') // This captures the name prop from component options, such as in b-form-input where "name" is passed as a prop
  const nameFromComponent = lodashGet(vnode, 'componentOptions.tag', '')

  if (!nameFromAttr && !nameFromPropsData && !nameFromComponent) {
    throw new Error('You forgot to set the name attribute. Or you should use a vue component')
  }
  const name = nameFromAttr || nameFromPropsData || nameFromComponent
  return snakeCase(name)
}

function _getOtherInfo(vnode) {
  return snakeCase(lodashGet(vnode, 'data.attrs.aha-other-info', ''))
}

function _getCustomProps(binding) {
  return lodashGet(binding, 'value')
}

function _getTrackingEventName(vnode, event) {
  const action = eventActions[event] || 'undefined_action'
  const objectName = _getObjectName(vnode)
  const otherInfo = _getOtherInfo(vnode)

  // Follow new standard for tracking event name
  // https://ahaslides.atlassian.net/wiki/spaces/AT/pages/826834947/Event+Tracking+-+Naming+Guideline+2024#C%E1%BA%A5u-tr%C3%BAc-t%C3%AAn-s%E1%BB%B1-ki%E1%BB%87n
  let args = [action, objectName, otherInfo]
  args = args.filter((arg) => arg)
  return args.join('_')
}

function _handleUserAction(action) {
  if (action.type !== USER_ACTION_TYPE) return
  const {
    eventName, vnode, binding,
  } = action.payload
  const trackingEventName = _getTrackingEventName(vnode, eventName)
  const customProps = _getCustomProps(binding)

  mixpanelUtil.track(trackingEventName, customProps)
}

function _handleRouteChangeAction(action) {
  if (action.type !== ROUTE_CHANGE_ACTION_TYPE) return
  const { to } = action.payload
  const isRenderedInsideIframe = window.top !== window.self
  if (isRenderedInsideIframe) {
    return
  }
  const routeName = snakeCase(lodashGet(to, 'name', ''))
  const trackingEventName = `view_${routeName}_page`
  mixpanelContextUtils.setRouteContext(to)
  mixpanelUtil.track(trackingEventName)
}

function _handleToastAction(action) {
  if (action.type !== SHOW_TOAST_ACTION_TYPE) return
  const { type, name } = action.payload
  const toastName = snakeCase(name)
  const trackingEventName = `view_${toastName}_${type}_toast`
  mixpanelUtil.track(trackingEventName)
}

const mixpanelUpdateContextPlugin = (store) => {
  store.subscribe((mutation, state) => {
    // console.log(mutation.type)
    // console.log(state.slideActive)
    if (!SYNC_MUTATION_TYPES.includes(mutation.type)) return
    const contextSetter = mixpanelContextSetters[mutation.type]
    const contextData = state[MUTATION_STATES_MAP[mutation.type]]
    // console.log(mutation.type, ' data is: ', contextData)
    contextSetter(contextData)
  })

  store.subscribeAction((action) => {
    if (!mixpanelContextUtils.mixpanelAvailable) return
    try {
      _handleUserAction(action)
      _handleRouteChangeAction(action)
      _handleToastAction(action)
    } catch (error) {
      sentry.captureException(error)
    }
  })
}

export default mixpanelUpdateContextPlugin
